import {useMemo} from 'react';
import {Box} from '@mui/material';
import {FloorPlanItem} from '../types';
import {calculateSlotPositions} from '../utils/fabric-factory';
import {FABRIC_DEFAULTS} from '../utils/fabric-defaults';
import {useFloorplanContext} from '../hooks/useFloorplanContext';

interface Props {
    item: FloorPlanItem;
}

// small component to create a live preview of the table shape and its seats
export const PreviewTableIcon: React.FC<Props> = ({item}) => {
    const {showSeatsAroundTable} = useFloorplanContext();
    const tableData = item.table;

    const shape = tableData?.shape ?? 'rectangle';
    const tableCanvas = tableData?.canvas;
    const seats = tableData?.seats;

    const width = tableCanvas?.width ?? FABRIC_DEFAULTS.table.width;
    const height = tableCanvas?.height ?? FABRIC_DEFAULTS.table.height;
    const seatOffset = seats?.offset ?? FABRIC_DEFAULTS.table.seats.offset;
    const edges = seats?.edges ?? FABRIC_DEFAULTS.table.seats.edges;
    const rawSlots = seats?.slots ?? [];

    const slots = useMemo(() => {
        return calculateSlotPositions(shape, width, height, seatOffset, edges, rawSlots, seats?.circle);
    }, [shape, width, height, seatOffset, edges, rawSlots, seats?.circle]);

    if (!tableData) return null;

    const seatRadius = 15;
    const padding = showSeatsAroundTable ? seatOffset + 20 : 10;
    const svgWidth = width + padding * 2;
    const svgHeight = height + padding * 2;
    const cx = svgWidth / 2;
    const cy = svgHeight / 2;

    return (
        <Box sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} width={24}
                 height={24} style={{overflow: 'visible'}}>
                {shape === 'circle' ? (
                    <circle
                        cx={cx}
                        cy={cy}
                        r={Math.min(width, height) / 2}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={Math.max(2, Math.min(width, height) * 0.03)}
                    />
                ) : (
                    <rect
                        x={cx - width / 2}
                        y={cy - height / 2}
                        width={width}
                        height={height}
                        rx={4}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={Math.max(2, Math.min(width, height) * 0.03)}
                    />
                )}
                {showSeatsAroundTable && slots.map((slot) => (
                    <circle
                        key={slot.id}
                        cx={cx + slot.x}
                        cy={cy + slot.y}
                        r={seatRadius}
                        fill="currentColor"
                    />
                ))}
            </svg>
        </Box>
    );
};
